/**
 * Klaviyo Back In Stock Notification System
 * Handles out-of-stock product notifications via Klaviyo
 */

const KLAVIYO_CONFIG = {
  publicKey: 'MTDCz7',
  backInStockListId: 'XEpu3k',
  marketingListId: 'MbLjx8',
  apiUrl: 'https://a.klaviyo.com/onsite/identify'
};

class BackInStockKlaviyo {
  constructor() {
    this.modal = document.getElementById('BackInStockModal');
    this.notifyBtn = document.getElementById('notify-when-available-btn');
    this.form = document.getElementById('back-in-stock-form');
    this.currentVariant = null;
    this.productData = null;
    
    if (!this.modal) {
      console.warn('Back in stock modal not found');
      return;
    }
    
    // Product page notify button is optional - collection pages don't have it
    if (!this.notifyBtn) {
      console.warn('Back in stock notify button not found - likely on collection page');
    }
    
    this.init();
  }

  init() {
    // Listen for variant changes (product page only)
    if (this.notifyBtn) {
      this.setupVariantListener();
      
      // Handle notify button click
      this.notifyBtn.addEventListener('click', this.handleNotifyClick.bind(this));
    }
    
    // Handle modal close (works for both product and collection pages)
    const closeBtn = document.getElementById('ModalClose-BackInStockModal');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.closeModal.bind(this));
    }
    
    // Handle form submission (works for both product and collection pages)
    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
    
    // Set up email input listener to enable/disable submit button
    const emailInput = document.getElementById('bis-email');
    if (emailInput) {
      emailInput.addEventListener('input', this.handleEmailInput.bind(this));
    }
    
    // Close modal on backdrop click
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.closeModal();
        }
      });
    }
  }
  
  handleEmailInput(e) {
    const email = e.target.value.trim();
    const submitBtn = this.form?.querySelector('.back-in-stock-modal__submit');
    
    if (submitBtn) {
      // Enable button if email is valid, disable if not
      if (email && this.isValidEmail(email)) {
        submitBtn.disabled = false;
        submitBtn.style.opacity = '1';
      } else {
        submitBtn.disabled = true;
        submitBtn.style.opacity = '0.5';
      }
    }
  }

  setupVariantListener() {
    // Listen to the product form input changes (for size/color selection)
    const variantInputs = document.querySelectorAll('input[name*="option"], input[type="radio"][name*="Size"], input[type="radio"][name*="Color"]');
    variantInputs.forEach(input => {
      input.addEventListener('change', () => {
        // Small delay to ensure variant ID is updated
        setTimeout(() => {
          this.updateCurrentVariant();
        }, 50);
      });
    });

    // Also watch for the hidden variant ID input changes
    const variantIdInput = document.querySelector('input[name="id"]');
    if (variantIdInput) {
      // Use MutationObserver to detect when the value attribute changes
      const observer = new MutationObserver(() => {
        this.updateCurrentVariant();
      });
      observer.observe(variantIdInput, {
        attributes: true,
        attributeFilter: ['value']
      });
    }

    // Set initial variant
    this.updateCurrentVariant();
  }

  updateCurrentVariant() {
    try {
      // Get product data from script tag
      const productDataScript = document.getElementById('ProductData');
      if (productDataScript) {
        this.productData = JSON.parse(productDataScript.textContent);
      }

      // Get currently selected variant
      const variantIdInput = document.querySelector('input[name="id"]');
      if (variantIdInput && this.productData) {
        const variantId = parseInt(variantIdInput.value);
        this.currentVariant = this.productData.variants.find(v => v.id === variantId);
        this.updateButtonVisibility(this.currentVariant);
      }
    } catch (error) {
      console.error('Error updating current variant:', error);
    }
  }

  updateButtonVisibility(variant) {
    if (!variant || !this.notifyBtn) return;
    
    this.currentVariant = variant;
    const addToCartBtn = document.querySelector('product-form button[type="submit"]');
    
    // Show notify button if variant is out of stock
    if (!variant.available) {
      this.notifyBtn.classList.remove('hidden');
      if (addToCartBtn) {
        addToCartBtn.classList.add('hidden');
      }
    } else {
      this.notifyBtn.classList.add('hidden');
      if (addToCartBtn) {
        addToCartBtn.classList.remove('hidden');
      }
    }
  }

  async handleNotifyClick() {
    const isLoggedIn = this.notifyBtn.getAttribute('data-is-logged-in') === 'true';
    const customerEmail = this.notifyBtn.getAttribute('data-customer-email');
    
    // Check if user is logged in to Shopify
    if (isLoggedIn && customerEmail) {
      // User is logged in - directly subscribe them
      await this.subscribeToBackInStock(customerEmail, false);
      this.showSuccessState();
      return;
    }
    
    // Check if user is already identified in Klaviyo (via previous opt-in)
    const klaviyoEmail = await this.getKlaviyoIdentifiedEmail();
    if (klaviyoEmail) {
      // User is identified in Klaviyo - directly subscribe them
      console.log('User identified in Klaviyo:', klaviyoEmail);
      await this.subscribeToBackInStock(klaviyoEmail, false);
      this.showSuccessState();
      return;
    }
    
    // User is not logged in and not identified - show modal
    this.openModal();
  }
  
  async getKlaviyoIdentifiedEmail() {
    // Method 1: Check localStorage for Klaviyo identify data
    try {
      const klaviyoData = localStorage.getItem('klaviyo_identify');
      if (klaviyoData) {
        const parsed = JSON.parse(klaviyoData);
        if (parsed && parsed.$email) {
          console.log('Found Klaviyo email in localStorage:', parsed.$email);
          return parsed.$email;
        }
      }
    } catch (error) {
      console.warn('Error reading Klaviyo data from localStorage:', error);
    }
    
    // Method 2: Check Klaviyo's internal state via cookies
    try {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === '__kla_id') {
          // Decode the Klaviyo cookie to get email
          try {
            const decoded = decodeURIComponent(value);
            const emailMatch = decoded.match(/"email":"([^"]+)"/);
            if (emailMatch && emailMatch[1]) {
              console.log('Found Klaviyo email in cookie:', emailMatch[1]);
              return emailMatch[1];
            }
          } catch (e) {
            console.warn('Error decoding Klaviyo cookie:', e);
          }
        }
      }
    } catch (error) {
      console.warn('Error reading cookies:', error);
    }
    
    // Method 3: Check if _learnq has stored identification
    if (typeof _learnq !== 'undefined') {
      try {
        // Access the internal queue to check for identify calls
        for (let item of _learnq) {
          if (Array.isArray(item) && item[0] === 'identify' && item[1] && item[1].$email) {
            console.log('Found Klaviyo email in _learnq queue:', item[1].$email);
            return item[1].$email;
          }
        }
      } catch (error) {
        console.warn('Error checking _learnq queue:', error);
      }
    }
    
    console.log('No Klaviyo identified email found');
    return null;
  }

  openModal() {
    if (!this.modal || !this.currentVariant) return;
    
    // Populate modal with product info
    const productImage = this.notifyBtn.getAttribute('data-product-image');
    const productTitle = this.notifyBtn.getAttribute('data-product-title');
    const productId = this.notifyBtn.getAttribute('data-product-id');
    
    document.getElementById('bis-modal-product-image').src = productImage;
    document.getElementById('bis-modal-product-image').alt = productTitle;
    document.querySelector('.back-in-stock-modal__title').textContent = productTitle;
    document.getElementById('bis-modal-variant-title').textContent = this.currentVariant.title || '';
    document.getElementById('bis-variant-id').value = this.currentVariant.id;
    document.getElementById('bis-product-id').value = productId;
    
    // Reset form
    this.form.reset();
    document.getElementById('bis-success-message').classList.add('hidden');
    document.getElementById('bis-error-message').classList.add('hidden');
    
    // Disable submit button until valid email is entered
    const submitBtn = this.form?.querySelector('.back-in-stock-modal__submit');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.style.opacity = '0.5';
    }
    
    // Open modal
    this.modal.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    if (!this.modal) return;

    this.modal.removeAttribute('open');
    document.body.style.overflow = '';

    // Reset description to product page version
    const modalDescription = this.modal.querySelector('#bis-modal-description');
    if (modalDescription) {
      modalDescription.textContent = 'Enter your Email Address and we will notify you when the item becomes available.';
    }

    // Hide variant selector
    const variantSelectWrapper = this.modal.querySelector('.back-in-stock-modal__variant-select');
    if (variantSelectWrapper) {
      variantSelectWrapper.style.display = 'none';
    }

    // Clear collection page data from modal dataset
    delete this.modal.dataset.selectedVariantId;
    delete this.modal.dataset.selectedVariantTitle;
    delete this.modal.dataset.productId;
    delete this.modal.dataset.productTitle;
    delete this.modal.dataset.productImage;
  }

  async handleFormSubmit(e) {
    e.preventDefault();
    
    const submitBtn = this.form?.querySelector('.back-in-stock-modal__submit');
    const emailInput = document.getElementById('bis-email');
    const marketingOptInInput = document.getElementById('bis-marketing-opt-in');
    const successMessage = document.getElementById('bis-success-message');
    const errorMessage = document.getElementById('bis-error-message');
    
    // Validate elements exist
    if (!emailInput || !marketingOptInInput || !submitBtn) {
      console.error('Required form elements not found');
      return;
    }
    
    const email = emailInput.value.trim();
    const marketingOptIn = marketingOptInInput.checked;
    
    // Validate email
    if (!email || !this.isValidEmail(email)) {
      emailInput.style.borderColor = '#ef4444';
      setTimeout(() => {
        emailInput.style.borderColor = '';
      }, 2000);
      return;
    }
    
    // Hide previous messages
    successMessage?.classList.add('hidden');
    errorMessage?.classList.add('hidden');
    
    // Show loading state
    submitBtn.classList.add('loading');
    submitBtn.disabled = true;
    
    try {
      await this.subscribeToBackInStock(email, marketingOptIn);
      
      // Show success message
      successMessage?.classList.remove('hidden');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        this.closeModal();
      }, 2000);
      
    } catch (error) {
      console.error('Error subscribing to back in stock:', error);
      errorMessage?.classList.remove('hidden');
    } finally {
      submitBtn.classList.remove('loading');
      submitBtn.disabled = false;
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  async subscribeToBackInStock(email, marketingOptIn) {
    // Check if data is coming from collection page (modal dataset) or product page
    const modal = document.querySelector('#BackInStockModal');
    const fromCollectionPage = modal && modal.dataset.selectedVariantId;

    let variantId, variantTitle, productId, productTitle, productImage;

    if (fromCollectionPage) {
      // Collection page data from modal dataset
      variantId = modal.dataset.selectedVariantId;
      variantTitle = modal.dataset.selectedVariantTitle;
      productId = modal.dataset.productId;
      productTitle = modal.dataset.productTitle;
      productImage = modal.dataset.productImage;

      if (!variantId) {
        throw new Error('Please select a size');
      }
    } else {
      // Product page data from current variant and notify button
      if (!this.currentVariant) {
        throw new Error('No variant selected');
      }
      variantId = this.currentVariant.id;
      variantTitle = this.currentVariant.title || 'Default';
      productId = this.notifyBtn.getAttribute('data-product-id');
      productTitle = this.notifyBtn.getAttribute('data-product-title');
      productImage = this.notifyBtn.getAttribute('data-product-image');
    }
    
    try {
      // Step 1: Try Klaviyo's native back-in-stock subscription first
      // This will work if products are synced to Klaviyo's catalog
      const nativeBackInStockPayload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      };

      let nativeBackInStockSuccess = false;
      
      try {
        const nativeResponse = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${KLAVIYO_CONFIG.publicKey}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'revision': '2024-10-15'
            },
            body: JSON.stringify(nativeBackInStockPayload)
          }
        );

        if (nativeResponse.ok) {
          const nativeText = await nativeResponse.text();
          console.log('✅ Native Klaviyo back-in-stock subscription successful (Status:', nativeResponse.status, ')');
          nativeBackInStockSuccess = true;
        } else {
          const errorText = await nativeResponse.text();
          console.warn('Native back-in-stock subscription failed with status:', nativeResponse.status);
          console.warn('Error response:', errorText);
          console.warn('This may indicate the Shopify catalog is not synced to Klaviyo. Using fallback method.');
        }
      } catch (error) {
        console.warn('Native back-in-stock subscription failed, using fallback method:', error);
      }

      // Step 1b: Fallback - Subscribe to the back-in-stock list
      const backInStockListPayload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: KLAVIYO_CONFIG.backInStockListId
              }
            }
          }
        }
      };

      const backInStockResponse = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_CONFIG.publicKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify(backInStockListPayload)
        }
      );

      if (!backInStockResponse.ok) {
        const errorText = await backInStockResponse.text();
        console.error('Back in stock list subscription failed:', errorText);
        throw new Error(`Back in stock list subscription failed: ${backInStockResponse.status}`);
      }

      // 202 responses might have empty body
      const backInStockText = await backInStockResponse.text();
      const backInStockData = backInStockText ? JSON.parse(backInStockText) : {};
      console.log('✅ Back in stock list subscription successful (Status:', backInStockResponse.status, ')');

      // Step 2: Create/update profile with variant data
      const profilePayload = {
        data: {
          type: 'profile',
          attributes: {
            email: email,
            properties: {
              'Back In Stock Product ID': productId,
              'Back In Stock Product Title': productTitle,
              'Back In Stock Product Image': productImage,
              'Back In Stock Product URL': window.location.href,
              'Back In Stock Variant ID': variantId,
              'Back In Stock Variant Title': variantTitle,
              'Back In Stock Request Date': new Date().toISOString()
            }
          }
        }
      };

      const profileResponse = await fetch(
        `https://a.klaviyo.com/client/profiles/?company_id=${KLAVIYO_CONFIG.publicKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify(profilePayload)
        }
      );

      if (profileResponse.ok) {
        const profileText = await profileResponse.text();
        const profileData = profileText ? JSON.parse(profileText) : {};
        console.log('✅ Profile updated with back-in-stock data (Status:', profileResponse.status, ')');
      }

      // Step 3: Track event for Klaviyo flow triggers
      const eventPayload = {
        data: {
          type: 'event',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            },
            metric: {
              data: {
                type: 'metric',
                attributes: {
                  name: 'Subscribed to Back in Stock'
                }
              }
            },
            properties: {
              'Product ID': productId,
              'Product Title': productTitle,
              'Variant ID': variantId,
              'Variant Title': variantTitle,
              'Product URL': window.location.href,
              'Price': fromCollectionPage ? 0 : (this.currentVariant?.price / 100 || 0),
              'Currency': typeof Shopify !== 'undefined' ? Shopify.currency.active : 'USD',
              'Product Image': productImage
            }
          }
        }
      };

      const eventResponse = await fetch(
        `https://a.klaviyo.com/client/events/?company_id=${KLAVIYO_CONFIG.publicKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify(eventPayload)
        }
      );

      if (eventResponse.ok) {
        const eventText = await eventResponse.text();
        const eventData = eventText ? JSON.parse(eventText) : {};
        console.log('✅ Back in stock event tracked (Status:', eventResponse.status, ')');
      }

      // Step 4: If marketing opt-in is checked, subscribe to marketing list
      if (marketingOptIn) {
        const marketingPayload = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: KLAVIYO_CONFIG.marketingListId
                }
              }
            }
          }
        };

        const marketingResponse = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_CONFIG.publicKey}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'revision': '2024-10-15'
            },
            body: JSON.stringify(marketingPayload)
          }
        );

        if (!marketingResponse.ok) {
          console.warn('Marketing list subscription failed (continuing anyway)');
        } else {
          const marketingText = await marketingResponse.text();
          const marketingData = marketingText ? JSON.parse(marketingText) : {};
          console.log('✅ Marketing list subscription successful (Status:', marketingResponse.status, ')');
        }
      }

      // Use _learnq for additional tracking
      if (typeof _learnq !== 'undefined') {
        _learnq.push(['identify', {
          '$email': email,
          '$source': 'Back in Stock Form'
        }]);
        
        _learnq.push(['track', 'Subscribed to Back in Stock', {
          'Product ID': productId,
          'Product Title': productTitle,
          'Variant ID': variantId,
          'Variant Title': variantTitle,
          'Product URL': window.location.href,
          'Price': fromCollectionPage ? 0 : (this.currentVariant?.price / 100 || 0),
          'Currency': typeof Shopify !== 'undefined' ? Shopify.currency.active : 'USD'
        }]);
      }
      
      return true;
      
    } catch (error) {
      console.error('Klaviyo subscription error:', error);
      throw error;
    }
  }


  showSuccessState() {
    // Change button to green success state
    this.notifyBtn.classList.add('notify-success');
    this.notifyBtn.querySelector('.notify-btn-text').textContent = 'Notification activated!';
    this.notifyBtn.style.backgroundColor = '#10b981';
    this.notifyBtn.style.borderColor = '#10b981';
    this.notifyBtn.disabled = true;
    
    // Reset after variant change
    setTimeout(() => {
      const variantInputs = document.querySelectorAll('input[name*="option"], input[name="id"]');
      variantInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.resetButtonState();
        }, { once: true });
      });
    }, 100);
  }

  resetButtonState() {
    if (!this.notifyBtn) return;
    
    this.notifyBtn.classList.remove('notify-success');
    this.notifyBtn.querySelector('.notify-btn-text').textContent = 
      this.notifyBtn.getAttribute('data-original-text') || 'Notify me when available';
    this.notifyBtn.style.backgroundColor = '';
    this.notifyBtn.style.borderColor = '';
    this.notifyBtn.disabled = false;
  }
}

// Handle collection page "Notify when available" buttons
class CollectionBackInStock {
  constructor() {
    this.modal = document.querySelector('#BackInStockModal');
    if (!this.modal) {
      console.warn('Back in stock modal not found - skipping collection page handlers');
      return;
    }

    this.initCollectionButtons();
  }

  initCollectionButtons() {
    // Find all collection page notify buttons
    const collectionButtons = document.querySelectorAll('.notify.bis-link');
    
    collectionButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleCollectionButtonClick(e));
    });
  }

  handleCollectionButtonClick(event) {
    const button = event.currentTarget;
    const productId = button.dataset.productId;
    const productTitle = button.dataset.productTitle;
    const productImage = button.dataset.productImage;
    const productUrl = button.dataset.productUrl;
    const variantsBase64 = button.dataset.variants;

    // Parse variants
    let variants = [];
    try {
      const variantsJson = atob(variantsBase64);
      variants = JSON.parse(variantsJson);
    } catch (error) {
      console.error('Error parsing variants:', error);
      return;
    }

    // Open modal
    this.openModal(productId, productTitle, productImage, productUrl, variants);
  }

  openModal(productId, productTitle, productImage, productUrl, variants) {
    // Populate modal fields
    const modalProductTitle = this.modal.querySelector('#BackInStockModal-Heading');
    const modalProductImage = this.modal.querySelector('#bis-modal-product-image');
    const modalDescription = this.modal.querySelector('#bis-modal-description');
    const variantSelect = this.modal.querySelector('#bis-variant-select');
    const variantSelectWrapper = this.modal.querySelector('.back-in-stock-modal__variant-select');

    if (modalProductTitle) {
      modalProductTitle.textContent = productTitle;
    }

    if (modalProductImage && productImage) {
      modalProductImage.src = productImage;
      modalProductImage.alt = productTitle;
    }

    // Update description for collection page
    if (modalDescription) {
      modalDescription.textContent = 'Select your size and enter your Email Address and we will notify you when the item becomes available.';
    }

    // Store product data on modal for form submission
    this.modal.dataset.productId = productId;
    this.modal.dataset.productTitle = productTitle;
    this.modal.dataset.productImage = productImage;

    // Handle variant selection
    if (variants && variants.length > 0) {
      // Find out-of-stock variants
      const outOfStockVariants = variants.filter(v => !v.available);

      if (outOfStockVariants.length === 0) {
        // All variants available - shouldn't happen, but handle gracefully
        console.warn('All variants are available for this product');
        return;
      }

      if (outOfStockVariants.length === 1 && variants.length === 1) {
        // Single out-of-stock variant - pre-select it and hide dropdown
        if (variantSelectWrapper) {
          variantSelectWrapper.style.display = 'none';
        }
        this.modal.dataset.selectedVariantId = outOfStockVariants[0].id;
        this.modal.dataset.selectedVariantTitle = outOfStockVariants[0].title || 'Default';
      } else {
        // Multiple variants - show dropdown with only out-of-stock options
        if (variantSelectWrapper) {
          variantSelectWrapper.style.display = 'block';
        }
        
        if (variantSelect) {
          // Clear existing options
          variantSelect.innerHTML = '<option value="">Select size...</option>';
          
          // Add only out-of-stock variants
          outOfStockVariants.forEach(variant => {
            const option = document.createElement('option');
            option.value = variant.id;
            option.textContent = variant.title;
            variantSelect.appendChild(option);
          });

          // Add change listener to update selected variant
          variantSelect.addEventListener('change', (e) => {
            const selectedVariant = outOfStockVariants.find(v => v.id == e.target.value);
            if (selectedVariant) {
              this.modal.dataset.selectedVariantId = selectedVariant.id;
              this.modal.dataset.selectedVariantTitle = selectedVariant.title;
            }
          });
        }
      }
    }

    // Show modal
    this.modal.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BackInStockKlaviyo();
    new CollectionBackInStock();
  });
} else {
  new BackInStockKlaviyo();
  new CollectionBackInStock();
}
